// useLiveList
// -----------
// One shared hook for the staff list screens (live-lists overhaul, Batch C1). It
// replaces the "each screen re-fetches the whole table every 30s" pattern with a live,
// push-based list:
//
//   initial fetch  →  subscribe to postgres_changes on the table
//                  →  delta-apply each change to the row array (respecting the screen's
//                     scope + sort)  →  fallback poll (default 60s)
//                  →  reconcile fetch on (re)connect (catch anything missed offline).
//
// Realtime does NOT replace polling — it demotes it to a safety net. A WebSocket drops
// (laptop sleep, network change, idle hangup); the fallback poll and the reconnect
// reconcile are what keep the list correct across those. The delta-apply itself is the
// pure `applyDelta` reducer (services/liveListReducer.ts), unit-tested separately.
//
// Wiring the actual screens onto this hook is Batch C3; the DB publication enablement
// for request_tasks / requests is Batch C2. Until a table is in the supabase_realtime
// publication the subscription simply receives no events and the fallback poll carries
// the list — the hook fails open.
//
// TypeScript source — no .js re-export stub (new module, imported extensionless).

import type { RealtimeChannel } from "@supabase/supabase-js";
import React from "react";
import { applyDelta, type LiveEvent } from "../services/liveListReducer";
import { sameRows } from "../utils/sameRows";
import { getSupabaseClient } from "../utils/supabase";

export interface UseLiveListOptions<T> {
  // The Realtime/Postgres table to watch (e.g. "request_tasks" | "requests").
  table: string;
  // Full-list read — used for the initial load, the fallback poll, and the reconnect
  // reconcile. Returns null on no-client / error (state is then left untouched).
  fetch: () => Promise<T[] | null>;
  // Is a changed row relevant to THIS list? (one table backs several lists). Default: all.
  rowBelongs?: (row: T) => boolean;
  // Row identity. Default: the row's `id`.
  getId?: (row: Partial<T>) => string | number | null | undefined;
  // Ordering applied to the fetched list and after each delta.
  sort?: (a: T, b: T) => number;
  // Suspend the whole thing (e.g. a sub-view is open). Default true.
  enabled?: boolean;
  // Fallback poll cadence in ms. Default 60s (tune per the plan §7.5).
  pollMs?: number;
}

export interface UseLiveListResult<T> {
  rows: T[];
  loading: boolean;
  refresh: () => void;
  // Optimistically fold a local change (e.g. the row an action just returned) into the
  // list, through the SAME delta path as a Realtime event — so it's idempotent when
  // Realtime later delivers the same change. Gives instant action feedback without
  // waiting for the round-trip, and works even when Realtime isn't connected.
  applyChange: (event: LiveEvent<T>) => void;
}

export function useLiveList<T>(opts: UseLiveListOptions<T>): UseLiveListResult<T> {
  const { table, enabled = true, pollMs = 60000 } = opts;
  const [rows, setRows] = React.useState<T[]>([]);
  const [loading, setLoading] = React.useState(true);

  // Latest config held in refs so the single subscription/poll always use current values
  // without tearing down and re-subscribing when a parent re-renders with new closures.
  const fetchRef = React.useRef(opts.fetch);
  fetchRef.current = opts.fetch;
  const belongsRef = React.useRef(opts.rowBelongs);
  belongsRef.current = opts.rowBelongs;
  const sortRef = React.useRef(opts.sort);
  sortRef.current = opts.sort;
  const getIdRef = React.useRef(opts.getId);
  getIdRef.current = opts.getId;

  const defaultGetId = (r: Partial<T>) => (r as { id?: string | number }).id;

  // Full reconcile fetch — initial load, fallback poll, and reconnect catch-up. `quiet`
  // skips the loading flag (polls/reconnects don't flash the spinner). Compare-before-
  // setState so an unchanged reconcile keeps the same array reference (no churn).
  const reconcile = React.useCallback(async function (quiet: boolean) {
    const data = await fetchRef.current();
    if (data) {
      const s = sortRef.current;
      const next = s ? data.slice().sort(s) : data;
      setRows((prev) => (sameRows(prev, next) ? prev : next));
    }
    if (!quiet) setLoading(false);
  }, []);

  React.useEffect(
    function () {
      if (!enabled) return;
      let cancelled = false;
      let firstSubscribe = true;
      setLoading(true);
      reconcile(false);

      const client = getSupabaseClient();
      let channel: RealtimeChannel | null = null;
      if (client) {
        channel = client
          .channel(`live-${table}`)
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table },
            // Loose inline payload shape (same approach as useOSDWatch) — a full
            // RealtimePostgresChangesPayload is assignable to it.
            function (payload: {
              eventType?: "INSERT" | "UPDATE" | "DELETE";
              new?: Record<string, unknown>;
              old?: Record<string, unknown>;
            }) {
              if (cancelled || !payload.eventType) return;
              const eventType = payload.eventType;
              const getId = getIdRef.current || defaultGetId;
              const rowBelongs = belongsRef.current || (() => true);
              setRows((prev) =>
                applyDelta(
                  prev,
                  {
                    eventType,
                    new: payload.new as unknown as T,
                    old: payload.old as unknown as Partial<T>,
                  },
                  { rowBelongs, getId, sort: sortRef.current }
                )
              );
            }
          )
          .subscribe(function (status: string) {
            // The initial SUBSCRIBED is covered by reconcile(false) above; only a LATER
            // SUBSCRIBED means the socket reconnected — reconcile to catch missed events.
            if (status === "SUBSCRIBED") {
              if (firstSubscribe) {
                firstSubscribe = false;
                return;
              }
              if (!cancelled) reconcile(true);
            }
          });
      }

      // Fallback poll — the safety net Realtime demotes but never removes.
      const pollId = setInterval(function () {
        if (!cancelled) reconcile(true);
      }, pollMs);

      return function () {
        cancelled = true;
        clearInterval(pollId);
        if (client && channel) client.removeChannel(channel);
      };
    },
    [enabled, table, pollMs, reconcile]
  );

  const refresh = React.useCallback(
    function () {
      reconcile(true);
    },
    [reconcile]
  );

  const applyChange = React.useCallback(function (event: LiveEvent<T>) {
    const getId = getIdRef.current || defaultGetId;
    const rowBelongs = belongsRef.current || (() => true);
    setRows((prev) => applyDelta(prev, event, { rowBelongs, getId, sort: sortRef.current }));
  }, []);

  return { rows, loading, refresh, applyChange };
}
